import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { unwrap } from '@/api/envelope';
import { listOrganizations, staffActivity, staffActivityActors } from '@/api/generated';
import {
  ActivityTable,
  type ActivityQuery,
  type ActorsQuery,
} from '@/components/activity/ActivityTable';
import { PageHeader } from '@/components/ui/PageHeader';
import { STATUS_LABELS } from '@/lib/organization';

async function fetchEntries(query: ActivityQuery) {
  const reply = await staffActivity({ query, throwOnError: true });
  const data = unwrap(reply.data);
  return { items: data?.items ?? [], total: data?.pagination?.total ?? 0 };
}

async function fetchActors(query: ActorsQuery) {
  const reply = await staffActivityActors({ query, throwOnError: true });
  return unwrap(reply.data) ?? [];
}

/** The search parameter the account page's "View all activity" link sets. */
export const ORGANIZATION_PARAM = 'organization';

/**
 * The audit log across every organization, for Activist staff.
 *
 * The same table as the Super Admin's, plus an organization filter and column.
 * The filter lives in the address, so the account page can link here already
 * narrowed to one organization and the back button keeps it.
 */
export function StaffActivityPage() {
  const [params, setParams] = useSearchParams();
  const organizationId = params.get(ORGANIZATION_PARAM) ?? '';

  // The first hundred by name, deactivated ones included, so a closed
  // account's history can still be found.
  const organizations = useQuery({
    queryKey: ['staff', 'organizations', 'for-activity'],
    queryFn: async () => {
      const reply = await listOrganizations({
        query: { limit: 100, include_deleted: true, sort: 'name', direction: 'asc' },
        throwOnError: true,
      });
      return unwrap(reply.data)?.items ?? [];
    },
  });

  function choose(id: string) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set(ORGANIZATION_PARAM, id);
        else next.delete(ORGANIZATION_PARAM);
        return next;
      },
      { replace: true },
    );
  }

  const options = organizations.data ?? [];
  // An organization named in the address but not in the first hundred still
  // shows as chosen, rather than as a blank that looks like "any".
  const known = options.some((org) => org.id === organizationId);

  return (
    <Box>
      <PageHeader
        title="Activity"
        description="Every recorded change across every organization, and who made it."
      />
      <ActivityTable
        view="staff"
        fetchEntries={fetchEntries}
        fetchActors={fetchActors}
        showOrganization
        extraValues={{ organization_id: organizationId }}
        onClearExtra={() => choose('')}
        extraFilters={
          <TextField
            select
            size="small"
            label="Organization"
            value={organizationId}
            onChange={(event) => choose(event.target.value)}
            sx={{ minWidth: 220, width: 'auto' }}
          >
            <MenuItem value="">Any</MenuItem>
            {organizationId && !known ? (
              <MenuItem value={organizationId}>This organization</MenuItem>
            ) : null}
            {options.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.status === 'DEACTIVATED'
                  ? `${org.name} (${STATUS_LABELS.DEACTIVATED.toLowerCase()})`
                  : org.name}
              </MenuItem>
            ))}
          </TextField>
        }
      />
    </Box>
  );
}
